/**
 * Gluon API client for the mobile feature management page.
 *
 * Reuses the existing `PUT /v1/gluon/applications/features` endpoint —
 * already accepts arbitrary FeatureType values, so the new mobile flags
 * work without any backend change.
 */

import axios from 'axios'
import { ClientApplication, FeatureType, IFeature } from '@numeo/types-core'

interface Envelope<T> {
    success: boolean
    data: T
    message?: string
}

/** Mobile-scoped FeatureType values, in display order. Dispatcher tabs are now Flipt-controlled. */
export const MOBILE_DISPATCHER_FLAGS: FeatureType[] = []

export const MOBILE_DRIVER_FLAGS: FeatureType[] = [
    FeatureType.MobileDriverDashboard,
    FeatureType.MobileDriverLoads,
    FeatureType.MobileDriverOpportunities,
    FeatureType.MobileDriverScanner,
]

/** Friendlier display labels for the page. */
export const MOBILE_FEATURE_LABELS: Record<string, string> = {
    [FeatureType.MobileDriverDashboard]: 'Dashboard tab',
    [FeatureType.MobileDriverLoads]: 'Loads tab',
    [FeatureType.MobileDriverOpportunities]: 'Offers tab',
    [FeatureType.MobileDriverScanner]: 'Scanner tab',
}

interface ApplicationsListResponse {
    applications: ClientApplication[]
    total: number
    totalPages: number
}

export const mobileFeaturesApi = {
    /** Search for an application by projectName (substring match). */
    searchApplications: async (projectName: string): Promise<ClientApplication[]> => {
        const params = new URLSearchParams()
        if (projectName) params.append('projectName', projectName)
        params.append('limit', '20')
        const res = await axios.get<ApplicationsListResponse>(`/gluon/applications?${params.toString()}`)
        return res.data?.applications ?? []
    },

    /**
     * Update a single application's full feature flag list. Reuses the
     * existing endpoint that accepts the complete features array. Callers
     * should merge the mobile flag changes into the application's existing
     * features array before calling — never lose non-mobile flags.
     */
    updateFeatures: async (projectName: string, allFeatures: IFeature[]): Promise<{ success: boolean }> => {
        const res = await axios.put<Envelope<{ features: IFeature[] }> | { success: boolean }>(
            `/gluon/applications/features?projectName=${encodeURIComponent(projectName)}`,
            allFeatures
        )
        return { success: !!(res.data as Envelope<unknown>).success || !!(res.data as { success: boolean }).success }
    },
}
